import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EpcotConfig, MyBetsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { timeSpanToMS } from '@frontend/sports/common/core/utils/date';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { generateGuid } from '@frontend/sports/common/core/utils/string';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { DsButtonKind } from '@frontend/ui/button';
import { Store } from '@ngrx/store';
import { Decimal } from 'decimal.js';
import { ArcRestrictionService } from 'packages/sports/web/app/src/arc/arc-restriction.service';
import {
    EarlyPayoutTACSource,
    EarlyPayoutTacService,
    IEarlyPayoutTACTrackingData,
} from 'packages/sports/web/app/src/my-bets-base/early-payout/early-payout-tac.service';
import { NumpadDisplayService } from 'packages/sports/web/app/src/numpad/numpad-display.service';
import { Observable, combineLatest, filter, fromEvent, map, merge, takeUntil } from 'rxjs';

import { betslipSelector } from '../../../../base/store/selectors';
import { IBetslipRootState, IBetslipState } from '../../../../base/store/state';
import { BetslipV1Pick } from '../../../../core/picks/betslip-v1-pick';
import { IQuickDepositState } from '../../../quick-deposit/quick-deposit.state';
import { QuickDepositService } from '../../../quick-deposit/services/quick-deposit.service';
import BetslipErrorsActions from '../../../validation/actions';
import { BetslipError } from '../../../validation/errors/betslip-error';
import { selectAllCurrentErrors } from '../../../validation/selectors';
import { isNotifyUserChangeError, isPickStatusChangeError } from '../../../validation/services/utils/betslip-errors-utils';
import { EditBetActions, ISwapPickPayload } from '../../actions';
import { EditBetPickRefreshService } from '../../services/edit-bet-pick-refresh.service';
import { EditBetButtonState, EditBetPlaceButtonStateService } from '../../services/edit-bet-place-button-state.service';

enum ButtonEnabledState {
    None,
    ConfirmChanges,
}

@Component({
    selector: 'bs-betslip-edit-bet-place-button',
    templateUrl: './edit-bet-place-button.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./edit-bet-place-button.scss'],
    standalone: false,
})
export class BetslipEditBetPlaceButtonComponent implements OnInit {
    betslipState: IBetslipState;
    errors: BetslipError[];
    errors$: Observable<BetslipError[]>;
    betslipState$: Observable<IBetslipState>;

    enabledState: ButtonEnabledState = ButtonEnabledState.None;
    buttonStates = EditBetButtonState;
    buttonEnabledStates = ButtonEnabledState;
    private readonly epTimeoutDelay: number;
    isUserOnPlayBreak: boolean;
    isEpcotEnabled = this.epcotConfig.isEnabled;
    isQuickDepositLoaderEnabled: boolean;
    private readonly destroyRef = inject(DestroyRef);
    private readonly _doc = inject(DOCUMENT);

    constructor(
        private changeDetector: ChangeDetectorRef,
        private trackingService: TrackingService,
        public siteCore: Sitecore,
        private earlyPayoutTacService: EarlyPayoutTacService,
        private editBetPlaceButtonStateService: EditBetPlaceButtonStateService,
        private timerService: TimerService,
        private preMatchPickRefresher: EditBetPickRefreshService,
        public numpadDisplayService: NumpadDisplayService,
        private store: Store<IBetslipRootState>,
        myBetsConfig: MyBetsConfig,
        private arcRestrictionService: ArcRestrictionService,
        private epcotConfig: EpcotConfig,
        private quickDepositService: QuickDepositService,
    ) {
        this.epTimeoutDelay = myBetsConfig.earlyPayoutButtonResetTime ? timeSpanToMS(myBetsConfig.earlyPayoutButtonResetTime) : 5000;
        this.betslipState$ = store.select(betslipSelector);
        this.errors$ = store.select(selectAllCurrentErrors);
        this.arcRestrictionService.isUserOnPlayBreak$.pipe(takeUntilDestroyed()).subscribe((data) => {
            this.isUserOnPlayBreak = data;
            this.changeDetector.markForCheck();
        });
    }

    ngOnInit(): void {
        this.betslipState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
            this.betslipState = state;
            this.changeDetector.markForCheck();
        });

        this.errors$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((errors) => {
            this.errors = errors;
            this.changeDetector.markForCheck();
        });

        this.quickDepositService.isQuickDepositLoaderEnabled$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isEnabled) => {
            this.isQuickDepositLoaderEnabled = isEnabled;
            this.changeDetector.markForCheck();
        });
    }

    get isDepositProcessing(): boolean {
        const state = this.buttonState();

        return state === EditBetButtonState.ProcessingDeposit;
    }

    get quickDepositState(): IQuickDepositState {
        return this.betslipState.quickDeposit;
    }

    get showTotalStake(): boolean {
        const state = this.buttonState();

        return (
            state !== EditBetButtonState.Locked &&
            state !== EditBetButtonState.Processing &&
            state !== EditBetButtonState.MakeDeposit &&
            state !== EditBetButtonState.QuickDeposit &&
            state !== EditBetButtonState.ProcessingDeposit &&
            new Decimal(this.betslipState.types.editBet.addedStake).greaterThan(0)
        );
    }

    get totalStake(): Nullable<Decimal> {
        const editMyBetState = this.betslipState.types.editBet;
        if (editMyBetState.earlyPayoutStake === null) {
            return null;
        }

        return new Decimal(editMyBetState.earlyPayoutStake).add(editMyBetState.addedStake);
    }

    get buttonCaption(): string {
        const state = this.buttonState();
        if (state === EditBetButtonState.Locked) {
            return this.siteCore.betslip.editBet.editBetLocked;
        }
        if (state === EditBetButtonState.Processing) {
            return this.siteCore.betslip.editBet.editBetProcessing;
        }
        if (state === EditBetButtonState.Enabled) {
            if (this.enabledState === ButtonEnabledState.None) {
                return this.getSaveBetChangesTitle();
            }
            if (this.enabledState === ButtonEnabledState.ConfirmChanges) {
                return this.siteCore.betslip.editBet.confirmBetChanges;
            }
        }
        if (state === EditBetButtonState.MakeDeposit) {
            return this.siteCore.betslip.editBet.editBetDeposit;
        }

        if (state === EditBetButtonState.QuickDeposit) {
            return this.siteCore.betslip.editBet.editBetQuickDeposit;
        }
        if (state === EditBetButtonState.ProcessingDeposit) {
            return this.siteCore.betslip.editBet.editBetDepositProcessing;
        }

        return this.getSaveBetChangesTitle();
    }

    buttonState(): EditBetButtonState {
        return this.editBetPlaceButtonStateService.buttonState(
            this.betslipState.base,
            this.betslipState.types,
            this.betslipState.quickDeposit,
            this.errors,
        );
    }

    private getSaveBetChangesTitle(): string {
        return this.hasUserNotifyError() ? this.siteCore.betslip.editBet.saveBetChanges : this.siteCore.betslip.editBet.saveBetChanges1;
    }

    private askUserForAcceptTACIfNeeded(): Promise<boolean> {
        const hasUserAcceptedTAC = this.earlyPayoutTacService.hasUserAcceptedTAC;
        if (hasUserAcceptedTAC) {
            return Promise.resolve(true);
        }

        const containsLiveBets = this.betslipState.types.editBet.picks.pickList.some((p) => p instanceof BetslipV1Pick && p.event.isLive);
        const trackingData: IEarlyPayoutTACTrackingData = {
            source: EarlyPayoutTACSource.MyBets,
            forLiveBet: containsLiveBets,
        };

        return this.earlyPayoutTacService.acceptEarlyPayoutTaC(trackingData);
    }

    get isButtonEnabled(): boolean {
        const buttonState = this.buttonState();
        const a =
            !this.isUserOnPlayBreak &&
            (buttonState === EditBetButtonState.Enabled ||
                buttonState === EditBetButtonState.MakeDeposit ||
                (buttonState === EditBetButtonState.QuickDeposit && this.quickDepositState.isAvailable));

        return a;
    }

    private editBetDeposit(): void {
        this.store.dispatch(EditBetActions.makeDeposit());
    }

    private dispatchQuickDeposit(): void {
        this.store.dispatch(EditBetActions.deposit());
    }

    async buttonClick(event: Event): Promise<void> {
        event.stopPropagation();

        const requestId = generateGuid();

        const buttonState = this.buttonState();

        if (buttonState === EditBetButtonState.MakeDeposit) {
            this.editBetDeposit();
        }

        if (buttonState === EditBetButtonState.QuickDeposit) {
            this.dispatchQuickDeposit();
        }

        if (buttonState === EditBetButtonState.Enabled) {
            this.numpadDisplayService.close();

            if (this.enabledState === ButtonEnabledState.None) {
                // Show Early payout TAC if needed.
                const hasUserAcceptTAC = await this.askUserForAcceptTACIfNeeded();
                if (!hasUserAcceptTAC) {
                    return;
                }
                const isRemovedState = Object.keys(this.betslipState.types.editBet.picks.pickState).reduce(
                    (aggr, id) => {
                        aggr[id] = this.betslipState.types.editBet.picks.pickState[id].isRemoved;

                        return aggr;
                    },
                    {} as { [id: string]: boolean },
                ); // Get Is Removed State.

                // Accept all changes in betslip
                this.acceptChanges();
                // Validate betslip.
                this.validateBetslip();

                // Set button state to Confirm. Make it before calling updatePicks because
                // Angular is rendering the button during the request and shows 'Save Changes', but should go to `Confirm Changes`.
                this.enabledState = ButtonEnabledState.ConfirmChanges;

                // Refresh the picks.
                const updates = await this.preMatchPickRefresher.updatePicks(this.betslipState);

                if (updates.length > 0) {
                    this.restoreIsRemovedState(isRemovedState, updates);

                    // If some of the picks were updated. Validate it again.
                    this.validateBetslip();

                    if (this.hasPreventConfirmError()) {
                        // If there is an error after swap return;
                        this.enabledState = ButtonEnabledState.None;

                        return;
                    }
                }

                // Start the timer after enabled state is set to Confirm.
                this.startTimer()
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: void 0,
                        error: void 0,
                        complete: () => this.resetState(),
                    });

                this.trackSaveChangesClick();
                this.changeDetector.markForCheck();
            } else if (this.enabledState === ButtonEnabledState.ConfirmChanges) {
                this.enabledState = ButtonEnabledState.None;
                this.placeBet(requestId);
            }
        }
    }

    private hasUserNotifyError(): boolean {
        return this.errors.some((error) => isNotifyUserChangeError(error));
    }

    private hasPreventConfirmError(): boolean {
        const errors = this.errors;

        return errors.some((er) => isPickStatusChangeError(er)) || !this.editBetPlaceButtonStateService.canPlaceBet(this.betslipState.types, errors);
    }

    private startTimer(): Observable<number> {
        const startErrors = this.errors;

        const state$ = combineLatest([this.betslipState$, this.errors$]);

        const resetNotifier = merge(
            // User click on document but not on the button. On button we have stopPropagation, so button click should not trigger document click.
            fromEvent<MouseEvent>(this._doc, 'click'),
            // Current betslip state has new errors or bet can not be placed.
            state$.pipe(
                map(([state, errors]) => {
                    return {
                        errorsCount: errors.length,
                        canPlace: this.editBetPlaceButtonStateService.canPlaceBet(state.types, errors),
                    };
                }),
                filter((value) => startErrors.length < value.errorsCount || !value.canPlace),
            ),
        );

        return this.timerService.timer(this.epTimeoutDelay).pipe(takeUntil(resetNotifier));
    }

    private resetState(): void {
        if (this.enabledState === ButtonEnabledState.ConfirmChanges) {
            this.enabledState = ButtonEnabledState.None;
            this.changeDetector.markForCheck();
            this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_SaveBetChanges_Reset',
                [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
            });
        }
    }

    private acceptChanges(): void {
        this.store.dispatch(BetslipErrorsActions.acceptBetslipEditBetChanges());
        this.store.dispatch(EditBetActions.acceptCurrentEarlyPayoutValue());
    }

    private validateBetslip(): void {
        this.store.dispatch(BetslipErrorsActions.validateBetslip());
    }

    private placeBet(requestId: string): void {
        this.store.dispatch(EditBetActions.placeBet({ payload: { requestId } }));
    }

    private restoreIsRemovedState(savedState: { [id: string]: boolean }, swaps: ISwapPickPayload[]): void {
        swaps
            .filter((sw) => !sw.oldPick.id.isEqual(sw.newPick.id) || sw.newPick.market.isVisible)
            .filter((sw) => savedState[sw.oldPick.id.toString()] === false)
            .map((sw) => EditBetActions.undoRemovePick({ pickId: sw.newPick.id.toString() }))
            .forEach((action) => {
                this.store.dispatch(action);
            });
    }

    private trackSaveChangesClick(): void {
        // this.actionButtonTrackingService.track(BetslipActionButtonTracking.SaveChanges, BetslipActionButtonTracking.EditMyBet);
        this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
            [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_SaveBetChanges_Click',
            [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
        });
    }

    get buttonKind(): DsButtonKind {
        if (
            this.enabledState === this.buttonEnabledStates.ConfirmChanges ||
            [
                EditBetButtonState.MakeDeposit,
                EditBetButtonState.QuickDeposit,
                EditBetButtonState.ProcessingDeposit,
                EditBetButtonState.Processing,
            ].includes(this.buttonState()) ||
            this.isEpcotEnabled
        ) {
            return 'primary';
        }

        return 'success';
    }
}
